import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateTodoInput,
  DeleteScope,
  UpdateTodoInput,
  Weekday,
  WeekKey,
  WeekResponse,
} from '@td/shared';
import { get, post, patch, del } from '../lib/api';

const weekKey = (week: WeekKey) => ['todos', week];

function useInvalidate() {
  const qc = useQueryClient();
  return (week: WeekKey) => {
    void qc.invalidateQueries({ queryKey: weekKey(week) });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useWeekTodos(week: WeekKey) {
  return useQuery({
    queryKey: weekKey(week),
    queryFn: () => get<WeekResponse>(`/todos?weekStart=${week}`),
    // Weeks are stable; keep them cached so paging back and forth is instant.
    staleTime: 60_000,
  });
}

export function useCreateTodo() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreateTodoInput) => post('/todos', input),
    onSuccess: (_d, input) => invalidate(input.weekStart),
  });
}

export function useUpdateTodo() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateTodoInput & { id: string; week: WeekKey }) => {
      const { week: _week, ...body } = input as UpdateTodoInput & { week: WeekKey };
      return patch(`/todos/${id}`, body);
    },

    // Checking a box should feel instant.
    onMutate: async ({ id, week, ...input }) => {
      await qc.cancelQueries({ queryKey: weekKey(week) });
      const previous = qc.getQueryData<WeekResponse>(weekKey(week));
      qc.setQueryData<WeekResponse>(weekKey(week), (old) =>
        old ? { ...old, todos: old.todos.map((t) => (t.id === id ? { ...t, ...input } : t)) } : old,
      );
      return { previous, week };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(weekKey(ctx.week), ctx.previous);
    },
    onSettled: (_d, _e, vars) => invalidate(vars.week),
  });
}

export function useDeleteTodo() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, week, scope }: { id: string; week: WeekKey; scope: DeleteScope }) =>
      del<void>(`/todos/${id}?scope=${scope}&weekStart=${week}`),
    onMutate: async ({ id, week }) => {
      await qc.cancelQueries({ queryKey: weekKey(week) });
      const previous = qc.getQueryData<WeekResponse>(weekKey(week));
      qc.setQueryData<WeekResponse>(weekKey(week), (old) =>
        old ? { ...old, todos: old.todos.filter((t) => t.id !== id) } : old,
      );
      return { previous, week };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(weekKey(ctx.week), ctx.previous);
    },
    onSettled: (_d, _e, vars) => invalidate(vars.week),
  });
}

/**
 * Move an item to another day, this week or next.
 *
 * Both weeks are refreshed: the one it left, which now has one fewer, and the
 * one it landed in — which is usually not the week on screen, and would
 * otherwise be a stale cache waiting to show the wrong list on Monday.
 */
export function useMoveTodo() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      id,
      weekday,
      week,
      fromWeek,
    }: {
      id: string;
      weekday: Weekday;
      week: WeekKey;
      fromWeek: WeekKey;
    }) => post(`/todos/${id}/move`, { weekday, weekStart: week, fromWeek }),
    onSuccess: (_d, vars) => {
      invalidate(vars.fromWeek);
      if (vars.week !== vars.fromWeek) invalidate(vars.week);
    },
  });
}

export function useStopRepeating() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, week }: { id: string; week: WeekKey }) =>
      post(`/todos/${id}/stop-repeating`, { weekStart: week }),
    onSuccess: (_d, vars) => invalidate(vars.week),
  });
}

export function useReorderTodos() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ weekday, week, ids }: { weekday: Weekday; week: WeekKey; ids: string[] }) =>
      post('/todos/reorder', { weekday, weekStart: week, ids }),
    onSettled: (_d, _e, vars) => invalidate(vars.week),
  });
}
